import { Trash2, BookOpen, AlertCircle } from 'lucide-react';
import { formatGenEdCodes } from '@/utils/genEdEntry';

export interface AwardedCourse {
  code: string;
  credits?: number;
  gen_ed_codes?: string[];
}

export interface APScore {
  id: number | string;
  exam_name: string;
  score: number | string;
  year_taken?: number | string;
  requires_advisor?: boolean;
  superseded_by?: string | null;
  awarded_courses?: AwardedCourse[];
}

export interface APScoresListProps {
  scores: APScore[];
  saving?: boolean;
  onRemoveScore: (scoreId: APScore['id'], examName: string) => void;
}

const APScoresList = ({ scores, saving, onRemoveScore }: APScoresListProps) => {
  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-3">
        Current AP Scores ({scores.length})
      </h3>

      {scores.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-400" />
          <p>No AP scores entered yet</p>
          <p className="text-sm">Add your first AP score above to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {scores.map((score) => (
            <div
              key={score.id}
              className={`p-3 border rounded-md ${
                score.superseded_by
                  ? 'border-gray-200 bg-gray-50 opacity-75'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                    <span
                      className={`font-medium ${
                        score.superseded_by ? 'text-gray-500' : 'text-gray-900'
                      }`}
                    >
                      {score.exam_name}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        score.superseded_by
                          ? 'bg-gray-300 text-gray-600'
                          : 'bg-primary text-white'
                      }`}
                    >
                      Score: {score.score}
                    </span>
                    {score.year_taken && (
                      <span className="text-sm text-gray-500">{score.year_taken}</span>
                    )}
                    {score.requires_advisor && !score.superseded_by && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Advisor Required
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onRemoveScore(score.id, score.exam_name)}
                  disabled={saving}
                  className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-50 transition-colors flex-shrink-0"
                  title="Remove this AP score"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {score.superseded_by ? (
                <div className="mt-2 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-500">
                    No credit awarded — superseded by {score.superseded_by}
                  </span>
                </div>
              ) : (
                score.awarded_courses &&
                score.awarded_courses.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">Awards:</span>
                    {score.awarded_courses.map((course, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                      >
                        {course.code}
                        {course.credits != null && course.credits > 0 && (
                          <span className="ml-1 text-green-600">({course.credits}cr)</span>
                        )}
                        {course.gen_ed_codes && course.gen_ed_codes.length > 0 && (
                          <span className="ml-1 text-green-600">
                            [{formatGenEdCodes(course.gen_ed_codes)}]
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default APScoresList;
